import { useEffect, useState } from "react";
import TablaCotizacionesVista from "../vista/tablaCotizacionesVista";
import type { ICotizacionNBS } from "../../../modelos/CotizadorNBS/entidades/ICotizacionNBS";
import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, type SortingState } from "@tanstack/react-table";
import { columnasTablaCotizaciones } from "../vista/constantesTablaCotizaciones";
import { estadoInicialCotizacionNBS } from "../interfaces";
import { CotizadorModelo } from "../../../modelos/CotizadorNBS";
import { accionTransicionEstado, nombreEstadoCotizacion, type EstadoCotizacionNBSId } from "../../../modelos/CotizadorNBS/entidades/EstadoCotizacionNBS";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { EnumTipoNotificacion } from "../../../globales/interfaces";
import ModeloUsuario from '../../../modelos/Usuario';
import { useAutenticacion } from '../../../globales/contextos/authContext';
import { usePermisos } from '../../../globales/contextos/usePermisos';
import { EnumPermisos } from '../../../globales/enums/enumPermisos';

import { ProveedorCargando, useCargando } from "../../../globales/contextos/cargandoContext";
import { useConfirmacion } from "../../../globales/contextos/confirmacionContext";
import { usePdfCotizacion } from "./usePdfCotizacion";

interface CotizadorCVProps {
  alCrearNueva?: () => void;
  alEditar?: (cotizacion: ICotizacionNBS) => void;
}

const CotizadorCV: React.FC<CotizadorCVProps> = ({ alCrearNueva, alEditar }) => {
  const modeloCotizador = new CotizadorModelo();
  const modeloUsuario = new ModeloUsuario();
  const { usuario } = useAutenticacion();
  const { tienePermiso } = usePermisos();
  // Asesores comerciales: solo sus cotizaciones. Administrador y gerencia: todas.
  const verTodas = tienePermiso(EnumPermisos.PERMISO_COTIZADOR_NBS_VER_TODAS);

  const { mostrarNotificacion } = useNotificaciones();

  const { manejarCargando } = useCargando();
  const { confirmar } = useConfirmacion();
  const pdf = usePdfCotizacion();

  const PAGE_SIZE = 15;
  const [pageIndex, setPageIndex] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);

  const [listaCotizaciones, setListaCotizaciones] = useState<ICotizacionNBS[]>([]);
  // El back pagina: trae solo la página pedida y el total en Pagination.
  const [totalCotizaciones, setTotalCotizaciones] = useState(0);
  const [habilitarFiltros, setHabilitarFiltros] = useState<boolean>(false);
  const [filtroCotizacion, setFiltroCotizacion] = useState<ICotizacionNBS>(estadoInicialCotizacionNBS);

  useEffect(() => {
    consultarCotizaciones();
  }, [pageIndex]);

  const consultarCotizaciones = async () => {
    try {
      manejarCargando(true, "Consultando cotizaciones...", false);
      const paisId = usuario?.PaisId || undefined;
      const estadoId = (filtroCotizacion as any)?.Estado?.Id || undefined;
      const res = await modeloCotizador.obtenerListaCotizaciones(pageIndex + 1, PAGE_SIZE, paisId, estadoId, verTodas ? undefined : usuario?.Id);
      if (res && res.Data) {
        // Las rechazadas se muestran (etiqueta roja) para poder reabrirlas desde la fila.
        const cotizacionesFiltradas = res.Data;

        // Traer todos los usuarios del país de una sola vez
        const usuariosMap: Record<number, string> = {};
        try {
          const resUsuarios = await modeloUsuario.obtenerListaUsuarios({ PaisId: paisId });
          if (resUsuarios?.Data) {
            resUsuarios.Data.forEach((u: any) => {
              if (u.Id && u.NombreCompleto) {
                usuariosMap[u.Id] = u.NombreCompleto;
              }
            });
          }
        } catch (e) {
          console.error('Error obteniendo lista de usuarios', e);
        }

        // Mapear los nombres a las cotizaciones filtrando por UsuarioCreadorId
        const cotizacionesConUsuario = cotizacionesFiltradas.map((c: any) => ({
          ...c,
          UsuarioCreadorNombre: usuariosMap[c.UsuarioCreadorId] || `ID:${c.UsuarioCreadorId}`
        }));

        setListaCotizaciones(cotizacionesConUsuario);
        setTotalCotizaciones(res.Pagination?.Total ?? cotizacionesConUsuario.length);
      }
    }
    catch {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error al consultar cotizaciones',
        'No fue posible consultar las cotizaciones. Por favor verifica tu conexión e intenta nuevamente.',
        5000
      );
    }
    finally {
      manejarCargando(false);
    }
  }

  const manejarHabilitarFiltros = () => {
    setHabilitarFiltros(prev => !prev);
  }

  const manejarCambioFiltroCotizacion = async (campo: string, valor: any) => {
    setFiltroCotizacion((prevFiltro) => ({
      ...prevFiltro,
      [campo]: valor
    }));
  }



  const tabla = useReactTable({
    data: listaCotizaciones,
    columns: [],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // La página ya viene del back: la tabla no vuelve a cortar los datos.
    manualPagination: true,
    state: { pagination: { pageIndex, pageSize: PAGE_SIZE }, sorting },
    onPaginationChange: updater => {
      if (typeof updater === 'function') {
        const newState = updater({ pageIndex, pageSize: PAGE_SIZE });
        setPageIndex(newState.pageIndex);
      } else {
        setPageIndex(updater.pageIndex ?? 0);
      }
    },
    pageCount: Math.max(1, Math.ceil(totalCotizaciones / PAGE_SIZE)),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
  });

  const columns = columnasTablaCotizaciones({
    manejarEdicionCotizacion: (cotizacion: ICotizacionNBS) => {
      if (alEditar) {
        alEditar(cotizacion);
      }
    },
    // Con el cálculo guardado; el aviso de matrices desactualizadas y el recálculo están en el Resumen.
    // El back rechaza la cotización si el cálculo está incompleto o tiene cambios sin recalcular.
    manejarPrevisualizarPdf: (cotizacion: ICotizacionNBS) => { if (cotizacion.Id) pdf.previsualizar(cotizacion.Id); },
    manejarDescargarPdf: (cotizacion: ICotizacionNBS) => { if (cotizacion.Id) pdf.descargar(cotizacion.Id); },
    manejarCambioEstado: async (cotizacion: ICotizacionNBS, estado: EstadoCotizacionNBSId) => {
      const cotizacionId = cotizacion.Id;
      if (!cotizacionId) return;
      const accion = accionTransicionEstado(cotizacion.Estado?.Id as number, estado);
      const confirmado = await confirmar({
        titulo: accion.etiqueta,
        mensaje: `${cotizacion.NombreProyecto ?? 'Cotización'}: ${accion.confirmacion}`,
        tipo: EnumTipoNotificacion.Warning,
        textoAceptar: accion.etiqueta,
        textoCancelar: 'Cancelar'
      });
      if (!confirmado) return;

      try {
        manejarCargando(true, "Actualizando estado...");
        await modeloCotizador.cambiarEstadoCotizacion(cotizacionId, estado);
        mostrarNotificacion(EnumTipoNotificacion.Success, "Estado actualizado", `La cotización está en ${nombreEstadoCotizacion(estado)}.`);
        consultarCotizaciones();
      } catch (error: any) {
        mostrarNotificacion(EnumTipoNotificacion.Error, "Error", error?.mensajeBackend || "No se pudo cambiar el estado de la cotización");
      } finally {
        manejarCargando(false);
      }
    },
    manejarEliminacionCotizacion: async (cotizacion: ICotizacionNBS) => {
      const cotizacionId = cotizacion.Id;
      if (!cotizacionId) return;
      
      const confirmado = await confirmar({
        titulo: 'Eliminar Cotización',
        mensaje: `¿Estás seguro de que deseas eliminar la cotización de ${cotizacion.NombreProyecto ?? cotizacion.Proyecto?.Nombre ?? 'esta cotización'}? Esta acción no se puede deshacer.`,
        tipo: EnumTipoNotificacion.Warning,
        textoAceptar: 'Eliminar',
        textoCancelar: 'Cancelar'
      });

      if (confirmado) {
        try {
          manejarCargando(true, "Eliminando cotización...");
          const res: any = await modeloCotizador.eliminarCotizacion(cotizacionId);
          if (res && (res.NotificationType === EnumTipoNotificacion.Success || res.notificationType === EnumTipoNotificacion.Success || res.Data === true || res.data === true)) {
            mostrarNotificacion(EnumTipoNotificacion.Success, "Éxito", "Cotización eliminada correctamente");
            consultarCotizaciones();
          } else {
            mostrarNotificacion(EnumTipoNotificacion.Error, "Error", "No fue posible eliminar la cotización");
          }
        } catch (error) {
          mostrarNotificacion(EnumTipoNotificacion.Error, "Error", "Ocurrió un error al intentar eliminar la cotización");
        } finally {
          manejarCargando(false);
        }
      }
    }
  });

  tabla.setOptions((prev) => ({ ...prev, columns }));

  return (
    <>
      <ProveedorCargando>
        <TablaCotizacionesVista
          tabla={tabla}
          consultarCotizaciones={consultarCotizaciones}
          filtro={filtroCotizacion}
          habilitarFiltros={habilitarFiltros}
          manejarCambioFiltroCotizacion={manejarCambioFiltroCotizacion}
          manejarHabilitarFiltros={manejarHabilitarFiltros}

          alCrearNueva={alCrearNueva}
        />
      </ProveedorCargando>
      {pdf.modalPdf}
    </>
  )
}

export default CotizadorCV;
